import { isArray, isEmpty } from "lodash";

/**
 * Waits for when a DOM element is available
 * @param elementOrSelector
 * @param timeout
 * @return {Promise<unknown>}
 */
export const waitForDOMElReady = (elementOrSelector, timeout = 5000) => {
	return new Promise((resolve, reject) => {
		const i = setInterval(() => {
			let el;
			if (typeof elementOrSelector === "string") {
				el = document.querySelector(elementOrSelector);
			} else {
				el = elementOrSelector;
			}
			if (el !== null) {
				clearInterval(i);
				resolve(el);
			}
		}, 10);

		setTimeout(() => {
			clearInterval(i);
			if (process.env.REACT_APP_ENV !== "prod") {
				reject("element was not ready before timing out");
			}
		}, timeout);
	});
};

export const transferSVGProperties = (
	el,
	svgToCopy,
	style = {},
	props = {},
	color = null,
	filled = false,
	fillOutline = false,
	className = ""
) => {
	if (!el) return;

	// Copy all SVG attributes
	[...svgToCopy.attributes].forEach((attr) => {
		el.setAttribute(attr.nodeName, attr.nodeValue);
	});

	// Copy SVG code
	const svg = svgToCopy;

	// Add missing viewbox
	if (!svg.getAttribute("viewBox")) {
		const iconHeight = svg.getAttribute("height");
		const iconWidth = svg.getAttribute("width");
		svg.setAttribute("viewBox", `0 0 ${iconWidth} ${iconHeight}`);
	}

	// Copy SVG content
	el.innerHTML = svg.innerHTML;

	// Copy SVG attributes
	if (isArray(svg.attributes)) {
		svg.attributes.forEach((attr) => {
			el.setAttribute(attr.nodeName, attr.nodeValue);
		});
	}

	// For each prop, lets add to the <svg> also
	for (let prop in props) {
		if (prop === "className") {
			props[prop].split(" ").forEach((className) => {
				if (typeof el.classList !== "undefined" && className.length > 0) {
					el.classList.add(className);
				}
			});
			continue;
		}

		if (prop.match(/^on[A-Z].+$/)) {
			let event = prop.replace(/^on([A-Z].+)$/, "$1");
			event = event.charAt(0).toLowerCase() + event.slice(1);
			el.addEventListener(event, props[prop]);
			continue;
		}

		el.setAttribute(prop, props[prop]);
	}

	// Add icon name to class
	if (!isEmpty(className)) {
		className.split(" ").forEach((classItem) => {
			el.classList.add(classItem);
		});
	}

	// Get dimensions if available in SVG (should be)
	let width = el.attributes.getNamedItem("width");
	width = width ? width.value : null;
	let height = el.attributes.getNamedItem("height");
	height = height ? height.value : null;

	// If given only width, calculate the proportionate height
	if (props.width && !props.height && height && width) {
		const heightRatio = width / height;
		el.setAttribute("height", parseInt(props.width / heightRatio));
	}
	// If given only height, calculate the proportionate width
	if (props.height && !props.width && height && width) {
		const heightRatio = width / height;
		el.setAttribute("width", parseInt(props.height * heightRatio));
	}
	// if (height && width && !el.getAttribute("viewBox")) {
	//   el.setAttribute("viewBox", `0 0 ${width} ${height}`);
	// }
	// Add fill color class
	if (color && typeof el.classList !== "undefined") {
		el.classList.forEach((className) => {
			if (className.match(/^fill-[a-zA-Z-]+$/)) {
				el.classList.remove(className);
			}
		});
		el.classList.forEach((className) => {
			if (className.match(/^stroke-[a-zA-Z-]+$/)) {
				el.classList.remove(className);
			}
		});
		el.classList.add(`fill-${color}`);
		if (el.lastChild.classList) {
			el.lastChild.classList.add(`stroke-${color}`);
		}

		if (filled) {
			if (el.lastChild.classList) {
				el.lastChild.classList.add(`fill-${color}`);
			}
			if (fillOutline) {
				el.classList.remove(`fill-as-outline`);
			}
		} else {
			el.classList.remove(`fill-as-outline`);
			el.classList.add(`fill-as-outline`);
			if (el.lastChild.classList) {
				el.lastChild.classList.remove(`fill-${color}`);
			}
		}
	}

	// Add style
	Object.assign(el.style, style);
};

/**
 * Creates an element from HTML string
 * @param htmlString
 * @return {ChildNode}
 */
export const createElementFromHTML = (htmlString) => {
	var div = document.createElement("div");
	div.innerHTML = htmlString.trim();
	return div.firstChild;
};
